import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import React, { useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { VideoProvider } from 'src/shared/models/shared';

import { validUuid } from 'src/shared/helpers/uuidValidator';
import { useStartVideoSessionMutation } from '../documents/startVideoSession.generated';
import { JoinVideoSessionPage } from './JoinVideoSessionPage';
import { VideoSession } from './VideoSession';

interface MatchParams {
    uuid: string;
}

export const VideoSessionGuard: React.FC<RouteComponentProps<MatchParams>> = ({ match }) => {
    const flags = useFlags();

    const { uuid } = match.params;

    const [getVideoSessionInfo, { data, loading, called, error }] = useStartVideoSessionMutation({
        client: appClient,
    });

    const handleError = (message: string = config.messages.genericError) => {
        toastNotificationsService.renderErrorToast(message);
        navigationService.navigate('/');

        return null;
    };

    if (flags?.videoProvider === VideoProvider.ZoomLink) {
        if (validUuid(uuid)) {
            return <JoinVideoSessionPage sessionUuid={uuid} />;
        } else {
            return handleError('Could not join video session');
        }
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        if (flags?.videoProvider === VideoProvider.Tokbox) {
            getVideoSessionInfo({ variables: { sessionUuid: uuid } });
        }
    }, [uuid]);

    const noDataReceived = called && !loading && !data;

    if (error || noDataReceived) {
        return handleError(error && error.message);
    }

    if (!called || loading) {
        return <CenteredSpinner />;
    }

    const sessionInfo = get(data, 'getVideoSession.videoSession');

    if (
        !sessionInfo.apiKey ||
        !sessionInfo.sessionId ||
        !sessionInfo.sessionToken ||
        !sessionInfo.coachingVisit
    ) {
        return handleError("Couldn't get session information");
    }

    return (
        <VideoSession
            apiKey={sessionInfo.apiKey}
            sessionId={sessionInfo.sessionId}
            sessionToken={sessionInfo.sessionToken}
            sessionUuid={sessionInfo.coachingVisit.uuid}
        />
    );
};
